import React from 'react';
import { Globe, Award, Heart, CheckCircle, ArrowRight } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';

const About = () => {
    return (
        <div className="bg-white">
            <SEO
                pageKey="about"
                defaultTitle="About BPO Consultants | Our Story & Mission"
                defaultDescription="We are not just another BPO. We are your offshore strategy partners. Learn about our mission to democratize global talent for businesses of all sizes."
                defaultSchema={JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    "mainEntity": {
                        "@type": "Organization",
                        "name": "BPO Consultants",
                        "url": "https://bpoconsultants.ph",
                        "description": "Empowering businesses with elite offshore talent and strategic AI integration."
                    }
                }, null, 4)}
            />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#20222D] overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1200" alt="Team collaboration" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Democratizing Global Talent
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            We believe that every business, regardless of size, deserves access to the world's best talent.
                            <br className="hidden md:block" />
                            <strong>We are bridging the gap between ambition and execution.</strong>
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Our Story / Mission */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <FadeIn>
                            <img
                                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=800"
                                alt="Modern office environment"
                                className="rounded-2xl shadow-2xl"
                            />
                        </FadeIn>
                        <FadeIn delay={0.2}>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-6">Our Story</h2>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                BPO Consultants was born from a frustration with the traditional outsourcing model. Heavy fees, opaque pricing, and a "body shop" mentality that treated talent like commodities.
                            </p>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                We set out to build something different. A firm that acts as a strategic partner, not just a vendor. We focus on <strong>integration, not just delegation</strong>. We help you build teams that feel like they are sitting in the room next to you, even if they are 8,000 miles away.
                            </p>
                            <div className="space-y-4">
                                {[
                                    "Transparent, flat-rate pricing",
                                    "Top 1% talent selection process",
                                    "Dedicated operational support"
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center">
                                        <CheckCircle className="text-[#A2CB52] mr-3" size={24} />
                                        <span className="text-lg text-[#20222D] font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Why Philippines? */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#ECF5DC]/30">
                <div className="max-w-7xl mx-auto text-center">
                    <FadeIn>
                        <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-6">Why The Philippines?</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-16">
                            It's not just about cost. It's about culture, capability, and compatibility.
                        </p>
                    </FadeIn>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Globe size={48} />,
                                title: "Cultural Affinity",
                                text: "English is an official language. The work culture is heavily influenced by the West, ensuring seamless communication."
                            },
                            {
                                icon: <Award size={48} />,
                                title: "Highly Educated",
                                text: "The country produces over 600,000 college graduates annually, specializing in IT, Engineering, Healthcare, and Finance."
                            },
                            {
                                icon: <Heart size={48} />,
                                title: "Service Oriented",
                                text: "Filipinos are world-renowned for their empathy and hospitality, making them exceptional at customer support and care."
                            }
                        ].map((item, index) => (
                            <FadeIn key={index} delay={index * 0.1} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                                <div className="text-[#A2CB52] mb-6 flex justify-center">{item.icon}</div>
                                <h3 className="text-2xl font-bold text-[#20222D] mb-4">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{item.text}</p>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values / DNA */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <FadeIn className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-6">Our DNA</h2>
                    </FadeIn>
                    <div className="grid md:grid-cols-2 gap-8">
                        <FadeIn className="bg-[#20222D] p-10 rounded-2xl text-white">
                            <h3 className="text-2xl font-bold text-[#A2CB52] mb-4">Excellence over Volume</h3>
                            <p className="text-gray-300 text-lg leading-relaxed">
                                We would rather staff one perfect team member than ten mediocre ones. We are distinctively picky in our recruitment process.
                            </p>
                        </FadeIn>
                        <FadeIn className="bg-gray-50 p-10 rounded-2xl border border-gray-100">
                            <h3 className="text-2xl font-bold text-[#20222D] mb-4">People First</h3>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                We take care of our people so they take care of your business. Competitive pay, HMO, and career growth are non-negotiable.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto bg-[#A2CB52] rounded-3xl p-12 text-center shadow-2xl transform hover:scale-[1.01] transition-transform duration-300">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-6">Ready to Build Your Team?</h2>
                    <p className="text-xl text-[#20222D]/80 mb-8 max-w-2xl mx-auto font-medium">
                        Let's discuss your goals and how we can help you achieve them with a strategic offshore team.
                    </p>
                    <a
                        href="/contact"
                        className="inline-flex items-center bg-[#20222D] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-800 transition-all shadow-lg"
                    >
                        Start Your Journey
                        <ArrowRight className="ml-2" size={20} />
                    </a>
                </div>
            </section>
        </div>
    );
};

export default About;
